import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { PartialStore } from '~/store/types'

/**
 * A request to open the assistant-v2 panel on a conversation, from anywhere in the app. The panel lives in the
 * layout, outside any page's tree, so a page asks through the store; the panel opens the conversation and then
 * marks the request handled.
 */
type AssistantPanelState = {
  requestedConversationId: string | null
}

const initialState: AssistantPanelState = {
  requestedConversationId: null
}

const assistantPanelSlice = createSlice({
  name: 'assistantPanel',
  initialState,
  reducers: {
    conversationRequested: (state, action: PayloadAction<string>) => {
      state.requestedConversationId = action.payload
    },
    conversationRequestHandled: (state) => {
      state.requestedConversationId = null
    }
  }
})

export const { conversationRequested, conversationRequestHandled } = assistantPanelSlice.actions

export default assistantPanelSlice.reducer

const selectRequestedConversationId = (state: PartialStore<typeof assistantPanelSlice>) =>
  state.assistantPanel.requestedConversationId

/** The conversation a page has asked the panel to open, until the panel has opened it. */
export function useRequestedConversationId(): string | undefined {
  return useSelector(selectRequestedConversationId) ?? undefined
}

/** Opens the assistant-v2 panel on a conversation, in place on the current page. */
export function useOpenAssistantConversation(): (conversationId: string) => void {
  const dispatch = useDispatch()
  return useCallback((conversationId: string) => dispatch(conversationRequested(conversationId)), [dispatch])
}
